import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { PartialBlock } from "@blocknote/core";
import NoteEditor from "@/components/shared/NoteEditor";
import { X, ArrowUp, ArrowRight, ArrowDown, ArrowLeft, CalendarIcon, Search, User, Building2, Handshake, Sparkles, Plus, Trash2 } from "lucide-react";
import { Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { format, startOfDay } from "date-fns";
import { people, companies, deals, workspaceMembers, tasks as dummyTasks } from "@/data/dummy";
import { useTasks as useTasksContext } from "@/contexts/TasksContext";
import {
  useGetTaskLazyQuery,
  NotePriorityEnum,
  NoteStatusEnum,
  useNotesSubjectSearchLazyQuery,
  useGetUsersLazyQuery,
  UserSortEnum,
  Account,
  Contact,
  Deal,
} from "@/lib/graphql/types-and-hooks";
import { useTaskMutations } from "@/lib/hooks/use-task-mutations";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useFormatters } from "@/lib/i18n/useFormatters";
import { isLovablePreview } from "@/lib/utils/environment";
import { cn } from "@/lib/utils";
import UserAvatar from "@/components/shared/Avatar";
import CompanyLogo from "@/components/shared/CompanyLogo";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import ConfirmDeleteDialog from "@/components/shared/ConfirmDeleteDialog";

type TaskSubject = Account | Contact | Deal;

type AssocSubject = {
  id: string;
  type: "person" | "company" | "deal";
  name: string;
  sub?: string;
};

/**
 * Priority IDs, not labels. These strings are compared (`priority === "High"`),
 * mapped onto `NotePriorityEnum` and written into the preview TasksContext, so
 * they stay English on the wire; `PRIORITY_LABEL_KEYS` carries what the user
 * reads. Splitting the two is rule 2 of the localization program.
 */
const priorityCycle = ["High", "Medium", "Low"];

const PRIORITY_LABEL_KEYS: Record<string, string> = {
  High: "common.high",
  Medium: "common.medium",
  Low: "common.low",
};

/** Association subject type id → the label the badge renders (CSS uppercases it). */
const SUBJECT_TYPE_LABEL_KEYS: Record<AssocSubject["type"], string> = {
  person: "common.person",
  company: "common.company",
  deal: "common.deal",
};

const normalizeTaskTitle = (title: string) => title.trim().toLowerCase();

const onboardingNotesByTitle = new Map(
  dummyTasks
    .filter((task) => task.notes?.trim())
    .map((task) => [normalizeTaskTitle(task.title), task.notes as string])
);

const teamMembers = [
  ...workspaceMembers.map(m => ({ name: m.name.split(" ")[0], fullName: m.name })),
  ...people.map(p => ({ name: p.name.split(" ")[0], fullName: p.name })),
];
const uniqueMembers = Array.from(new Map(teamMembers.map(m => [m.fullName, m])).values());

function parseDate(s: string) {
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

interface TaskDetailSheetProps {
  taskId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hideOverlay?: boolean;
  onBack?: () => void;
}

export default function TaskDetailSheet({ taskId, open, onOpenChange, hideOverlay = false, onBack }: TaskDetailSheetProps) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const workspaceId = useWorkspaceId();
  const { tasks: contextTasks, updateTask: contextUpdateTask } = useTasksContext();
  const [getTask, { data: taskData, loading: taskLoading }] = useGetTaskLazyQuery();
  const { updateTask, deleteTask } = useTaskMutations();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [searchQuery, { data: searchData, loading: searchLoading }] = useNotesSubjectSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  // Ref to the inner sheet content — nested Radix Dialogs (the Meeting sheet
  // wraps this Task sheet) block pointer events on portaled children of an
  // ancestor dialog, so we portal popovers *into* this sheet's DOM instead.
  const sheetInnerRef = useRef<HTMLDivElement | null>(null);

  // Fetch task from backend when sheet opens and reset state when it closes
  useEffect(() => {
    if (open && taskId) {
      // Reset state FIRST to prevent showing stale data from previous task
      setIsDataLoaded(false);
      setPrevTaskId(null);
      setTitle("");
      setPriority("Medium");
      setDueDate("");
      setAssignee("");
      setNotes("");
      setNotesBlocks([{ type: "paragraph" as const }]);
      setBackendSubjects([]);
      setSubjects([]);
      setSearchValue("");
      setShowSearchDropdown(false);

      // Then fetch new task data
      if (workspaceId && !isLovablePreview()) {
        getTask({ variables: { id: taskId, workspaceId } });
      }
    } else if (!open) {
      // Reset ALL state when sheet closes
      setIsDataLoaded(false);
      setPrevTaskId(null);
      setTitle("");
      setPriority("Medium");
      setDueDate("");
      setAssignee("");
      setNotes("");
      setNotesBlocks([{ type: "paragraph" as const }]);
      setBackendSubjects([]);
      setSubjects([]);
      setSearchValue("");
      setShowSearchDropdown(false);
    }
  }, [open, taskId, workspaceId, getTask]);

  // Use backend task or fallback to context
  const backendTask = taskData?.note;
  const task = useMemo(() => {
    const contextTask = contextTasks.find(t => t.id === taskId);

    if (isLovablePreview()) {
      return contextTask;
    }

    if (backendTask) {
      // Format dueDate from backend format to display format
      let formattedDueDate = '';
      if (backendTask.dueDate) {
        const date = parseDate(backendTask.dueDate);
        if (date) {
          formattedDueDate = format(date, "MMM d, yyyy");
        }
      }

      return {
        id: backendTask.id!,
        title: backendTask.title || '',
        priority: backendTask.priority === NotePriorityEnum.High ? 'High'
                : backendTask.priority === NotePriorityEnum.Medium ? 'Medium'
                : 'Low',
        dueDate: formattedDueDate,
        assignee: backendTask.user?.preferredName || backendTask.user?.givenName || '',
        completed: backendTask.status === NoteStatusEnum.Completed,
        notes: backendTask.blocks || '',
        assignedBy: undefined as string | undefined,
        assignedAt: undefined as string | undefined,
        origin: undefined as "ai" | "manual" | undefined,
        aiSource: undefined as { meetingDate: string; personName: string } | undefined,
      };
    }

    return contextTask;
  }, [taskId, contextTasks, backendTask]);

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [notes, setNotes] = useState("");
  const [notesBlocks, setNotesBlocks] = useState<PartialBlock[]>([{ type: "paragraph" as const }]);
  const [backendSubjects, setBackendSubjects] = useState<TaskSubject[]>([]);
  const [subjects, setSubjects] = useState<AssocSubject[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notesSaveTimerRef = useRef<NodeJS.Timeout>();
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const assigneeInputRef = useRef<HTMLInputElement>(null);
  const hasChangesRef = useRef(false);
  const markChanged = useCallback(() => { hasChangesRef.current = true; }, []);

  useEffect(() => {
    if (assigneeOpen) {
      setAssigneeSearch("");
      setTimeout(() => assigneeInputRef.current?.focus(), 0);
    }
  }, [assigneeOpen]);

  // Real workspace members for assigning the task owner in production. Fetched
  // lazily only while the sheet is open so this always-mounted sheet doesn't
  // fire a users query on every page. Preview keeps the dummy member list.
  const [getUsers, { data: usersData }] = useGetUsersLazyQuery();
  useEffect(() => {
    if (open && !isLovablePreview() && workspaceId) {
      getUsers({
        variables: { filter: { workspaceId }, workspaceId, sort: [UserSortEnum.PreferredNameAsc] },
      });
    }
  }, [open, workspaceId, getUsers]);

  const assigneeMembers = useMemo<{ id?: string; fullName: string }[]>(() => {
    if (isLovablePreview()) return uniqueMembers.map(m => ({ fullName: m.fullName }));
    return (usersData?.users?.edges ?? [])
      .map(e => e?.node)
      .filter((u): u is NonNullable<typeof u> => !!u)
      .map(u => ({
        id: u.id,
        fullName: [u.preferredName || u.givenName, u.surname].filter(Boolean).join(' ') || u.email,
      }));
  }, [usersData]);

  const filteredAssigneeMembers = useMemo(
    () => assigneeMembers.filter(m => m.fullName.toLowerCase().includes(assigneeSearch.toLowerCase())),
    [assigneeSearch, assigneeMembers]
  );

  const handleSheetOpenChange = useCallback((next: boolean) => {
    if (!next && hasChangesRef.current) {
      // Flush any pending notes save immediately
      if (notesSaveTimerRef.current) {
        clearTimeout(notesSaveTimerRef.current);
        notesSaveTimerRef.current = undefined;
      }
      toast.success(t("detailSheets.taskUpdated"));
      hasChangesRef.current = false;
    }
    onOpenChange(next);
  }, [onOpenChange, t]);

  // Helper to build complete update payload (backend requires ALL fields always present)
  // Coffee-ui ALWAYS sends: title, blocks, dueDate, priority, status + association arrays
  // We need to send the COMPLETE current state, not just the changed field
  const buildUpdatePayload = useCallback((updates: Record<string, unknown>) => {
    const accountIds = backendSubjects.filter(s => s.__typename === 'Account').map(s => s.id!);
    const contactIds = backendSubjects.filter(s => s.__typename === 'Contact').map(s => s.id!);
    const dealIds = backendSubjects.filter(s => s.__typename === 'Deal').map(s => s.id!);

    // Build complete payload with ALL required fields (matching coffee-ui pattern)
    const payload: Record<string, unknown> = {
      // Core fields - use update value if provided, otherwise use current backend value
      title: updates.title ?? backendTask?.title ?? '',
      blocks: updates.blocks ?? backendTask?.blocks ?? '[]',
      priority: updates.priority ?? backendTask?.priority ?? NotePriorityEnum.Medium,
      dueDate: updates.dueDate !== undefined ? updates.dueDate : (backendTask?.dueDate ?? null),
      // Association arrays - ALWAYS include
      accountIds,
      contactIds,
      dealIds,
      engagementIds: [], // Always empty array for now
    };

    return payload;
  }, [backendSubjects, backendTask]);

  // Debounced backend search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchValue.trim() && workspaceId && !isLovablePreview()) {
      debounceTimerRef.current = setTimeout(() => {
        searchQuery({
          variables: {
            workspaceId,
            search: searchValue.trim(),
            first: 10,
          },
        });
      }, 300);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchValue, workspaceId, searchQuery]);

  // Backend search results
  const searchContacts = searchData?.contacts?.edges?.map(e => e?.node).filter(Boolean) || [];
  const searchAccounts = searchData?.accounts?.edges?.map(e => e?.node).filter(Boolean) || [];
  const searchDeals = searchData?.deals?.edges?.map(e => e?.node).filter(Boolean) || [];
  const hasBackendResults = searchContacts.length > 0 || searchAccounts.length > 0 || searchDeals.length > 0;

  // Dummy data fallback for preview mode
  const allItems = useMemo(() => {
    const items: AssocSubject[] = [];
    people.forEach(p => items.push({ id: p.id, type: "person", name: p.name, sub: p.company }));
    companies.forEach(c => items.push({ id: c.id, type: "company", name: c.name, sub: (c as any).website || "" }));
    deals.forEach(d => items.push({ id: d.id, type: "deal", name: d.name, sub: d.company }));
    return items;
  }, []);

  const filteredItems = useMemo(() => {
    // Only use dummy data in preview mode
    if (!isLovablePreview() || !searchValue.trim() || hasBackendResults) return [];
    const q = searchValue.toLowerCase();
    return allItems
      .filter(item => item.name.toLowerCase().includes(q) && !subjects.find(s => s.id === item.id && s.type === item.type))
      .slice(0, 8);
  }, [searchValue, allItems, subjects, hasBackendResults]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchDropdown]);

  const handleSubjectSelected = useCallback(async (subject: TaskSubject | AssocSubject) => {
    // Prevent operation until data is loaded
    if (!isDataLoaded || !task || !taskId) return;

    // Check if already selected
    if (subjects.find(s => s.id === subject.id)) return;

    // Handle backend subjects (Contact, Account, Deal)
    if ('__typename' in subject) {
      const typedSubject = subject as TaskSubject;

      // Add to backend subjects and auto-add associated accounts
      let newBackendSubjects = [...backendSubjects, typedSubject];

      // Auto-add associated accounts for contacts and deals
      if (typedSubject.__typename === "Contact" || typedSubject.__typename === "Deal") {
        const entity = typedSubject as Contact | Deal;
        const associatedAccounts = entity.accounts?.edges
          ?.map(edge => edge?.node)
          .filter((account): account is Account => !!account) || [];

        associatedAccounts.forEach(account => {
          if (!newBackendSubjects.find(s => s.id === account.id)) {
            newBackendSubjects = [...newBackendSubjects, account];
          }
        });
      }

      setBackendSubjects(newBackendSubjects);

      // Convert to UI subjects for display
      const uiSubject: AssocSubject = {
        id: typedSubject.id!,
        type: typedSubject.__typename === 'Contact' ? 'person'
            : typedSubject.__typename === 'Account' ? 'company'
            : 'deal',
        name: typedSubject.__typename === 'Contact'
          ? `${(typedSubject as Contact).properties?.firstName || ''} ${(typedSubject as Contact).properties?.lastName || ''}`.trim() || (typedSubject as Contact).primaryEmail || ''
          : typedSubject.__typename === 'Account'
          ? (typedSubject as Account).properties?.name || (typedSubject as Account).domain || ''
          : (typedSubject as Deal).properties?.name || '',
        sub: typedSubject.__typename === 'Contact'
          ? (typedSubject as Contact).accounts?.edges?.[0]?.node?.properties?.name || ''
          : typedSubject.__typename === 'Account'
          ? (typedSubject as Account).domain || ''
          : (typedSubject as Deal).accounts?.edges?.[0]?.node?.properties?.name || ''
      };
      setSubjects(prev => [...prev, uiSubject]);

      // Persist to backend if not in preview mode
      if (!isLovablePreview() && taskId) {
        const accountIds = newBackendSubjects.filter(s => s.__typename === 'Account').map(s => s.id!);
        const contactIds = newBackendSubjects.filter(s => s.__typename === 'Contact').map(s => s.id!);
        const dealIds = newBackendSubjects.filter(s => s.__typename === 'Deal').map(s => s.id!);

        await updateTask(taskId, { accountIds, contactIds, dealIds });
      }
    } else {
      // Handle dummy data subjects (preview mode)
      const assocSubject = subject as AssocSubject;
      setSubjects(prev => [...prev, assocSubject]);
    }

    setSearchValue("");
    setShowSearchDropdown(false);
  }, [subjects, backendSubjects, taskId, updateTask, task, isDataLoaded]);
  // Note: isLovablePreview is a utility function, not a reactive value

  const handleRemoveSubject = async (id: string, type: string) => {
    // Prevent operation until data is loaded
    if (!isDataLoaded || !task || !taskId) return;

    setSubjects(prev => prev.filter(s => !(s.id === id && s.type === type)));
    setBackendSubjects(prev => prev.filter(s => s.id !== id));

    // Persist to backend if not in preview mode
    if (!isLovablePreview() && taskId) {
      const newBackendSubjects = backendSubjects.filter(s => s.id !== id);
      const accountIds = newBackendSubjects.filter(s => s.__typename === 'Account').map(s => s.id!);
      const contactIds = newBackendSubjects.filter(s => s.__typename === 'Contact').map(s => s.id!);
      const dealIds = newBackendSubjects.filter(s => s.__typename === 'Deal').map(s => s.id!);

      await updateTask(taskId, { accountIds, contactIds, dealIds });
    }
  };

  const isEmptyNotesValue = useCallback((notesValue: string): boolean => {
    const trimmed = notesValue.trim();
    if (!trimmed) return true;

    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) return false;
      if (parsed.length === 0) return true;

      return parsed.every((block) => {
        if (!block || typeof block !== "object") return true;

        const content = (block as { content?: unknown }).content;

        if (typeof content === "string") {
          return content.trim().length === 0;
        }

        if (Array.isArray(content)) {
          return content.every((item) => {
            if (typeof item === "string") return item.trim().length === 0;
            if (item && typeof item === "object" && "text" in item) {
              return String((item as { text?: unknown }).text ?? "").trim().length === 0;
            }
            return true;
          });
        }

        return true;
      });
    } catch {
      return false;
    }
  }, []);

  const resolveTaskNotes = useCallback((taskTitle: string, notesValue: string): string => {
    if (!isEmptyNotesValue(notesValue)) return notesValue;
    return onboardingNotesByTitle.get(normalizeTaskTitle(taskTitle)) ?? notesValue;
  }, [isEmptyNotesValue]);

  // Helper to parse notes string into blocks
  const parseNotesString = useCallback((notesString: string): PartialBlock[] => {
    if (!notesString.trim()) return [{ type: "paragraph" as const }];

    // Try to parse as BlockNote JSON format
    try {
      const parsed = JSON.parse(notesString);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      // If parsing fails, treat as plain text (for backward compatibility or preview mode)
      console.warn('Failed to parse notes as JSON, treating as plain text:', error);
    }

    // Fallback: treat as plain text and convert to BlockNote format
    // Parse markdown links [text](url) into BlockNote partial link inline content
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;

    return notesString.split("\n").map((line) => {
      if (!line.trim()) return { type: "paragraph" as const, content: [] as const };

      const inlineContent: Array<string | { type: "link"; href: string; content: string }> = [];
      let lastIdx = 0;
      let match: RegExpExecArray | null;

      linkRe.lastIndex = 0;
      while ((match = linkRe.exec(line)) !== null) {
        if (match.index > lastIdx) {
          inlineContent.push(line.slice(lastIdx, match.index));
        }

        inlineContent.push({
          type: "link",
          href: match[2],
          content: match[1],
        });

        lastIdx = match.index + match[0].length;
      }

      if (lastIdx < line.length) {
        inlineContent.push(line.slice(lastIdx));
      }

      return {
        type: "paragraph" as const,
        content: inlineContent.length > 0 ? inlineContent : [line],
      };
    });
  }, []);

  // Debounced save for notes
  const handleNotesChange = useCallback((newBlocks: PartialBlock[]) => {
    if (!isDataLoaded || !task || !taskId) return;

    setNotesBlocks(newBlocks);

    // Clear existing timer
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
    }

    // Debounce save by 1 second
    notesSaveTimerRef.current = setTimeout(async () => {
      const blocksJson = JSON.stringify(newBlocks);
      markChanged();
      if (isLovablePreview()) {
        contextUpdateTask(taskId, { notes: blocksJson });
      } else {
        const payload = buildUpdatePayload({ blocks: blocksJson });
        await updateTask(taskId, payload);
      }
    }, 1000);
  }, [isDataLoaded, task, taskId, updateTask, contextUpdateTask, buildUpdatePayload, markChanged]);

  // Update local state when task changes
  const [prevTaskId, setPrevTaskId] = useState<string | null>(null);
  useEffect(() => {
    // Only load data if:
    // 1. Task ID changed (taskId !== prevTaskId)
    // 2. We have task data
    // 3. The task data matches the current taskId (prevents race condition)
    if (taskId !== prevTaskId && task && task.id === taskId) {
      setPrevTaskId(taskId);
      setTitle(task.title || "");
      setPriority(task.priority || "Medium");
      setDueDate(task.dueDate || "");
      setAssignee(task.assignee || "");

      const resolvedNotes = resolveTaskNotes(task.title || "", task.notes || "");
      setNotes(resolvedNotes);
      setNotesBlocks(parseNotesString(resolvedNotes));
      setIsDataLoaded(true); // Mark data as loaded

      if (isLovablePreview() && resolvedNotes !== (task.notes || "")) {
        contextUpdateTask(task.id, { notes: resolvedNotes });
      }

      // Load associations from backend data
      if (backendTask && !isLovablePreview()) {
        const backendSubs: TaskSubject[] = [];
        backendTask.contacts?.filter(c => c).forEach(c => backendSubs.push(c as Contact));
        backendTask.accounts?.filter(a => a).forEach(a => backendSubs.push(a as Account));
        backendTask.deals?.filter(d => d).forEach(d => backendSubs.push(d as Deal));
        setBackendSubjects(backendSubs);

        // Also update UI subjects for display
        const uiSubjects: AssocSubject[] = [];
        backendTask.contacts?.filter(c => c).forEach(c => {
          const name = `${c.properties?.firstName || ''} ${c.properties?.lastName || ''}`.trim() || c.primaryEmail || '';
          const sub = (c as any).accounts?.edges?.[0]?.node?.properties?.name || '';
          uiSubjects.push({ id: c.id!, type: "person", name, sub });
        });
        backendTask.accounts?.filter(a => a).forEach(a => {
          const name = a.properties?.name || a.domain || '';
          const sub = a.domain || '';
          uiSubjects.push({ id: a.id!, type: "company", name, sub });
        });
        backendTask.deals?.filter(d => d).forEach(d => {
          const name = d.properties?.name || '';
          const sub = (d as any).accounts?.edges?.[0]?.node?.properties?.name || '';
          uiSubjects.push({ id: d.id!, type: "deal", name, sub });
        });
        setSubjects(uiSubjects);
      } else {
        // For preview mode or when no backend data, use context task's linked arrays
        const contextTask = contextTasks.find(t => t.id === taskId);
        if (contextTask) {
          const newSubjects: AssocSubject[] = [];
          const linkedPeople = (contextTask as { linkedPeople?: string[] }).linkedPeople || [];
          linkedPeople.forEach((name: string) => {
            const p = people.find(pp => pp.name === name);
            if (p) newSubjects.push({ id: p.id, type: "person", name: p.name, sub: p.company });
          });
          const linkedCompanies = (contextTask as { linkedCompanies?: string[] }).linkedCompanies || [];
          linkedCompanies.forEach((name: string) => {
            const c = companies.find(cc => cc.name === name);
            if (c) newSubjects.push({ id: c.id, type: "company", name: c.name, sub: (c as any).website || "" });
          });
          const linkedDeals = (contextTask as { linkedDeals?: string[] }).linkedDeals || [];
          linkedDeals.forEach((name: string) => {
            const d = deals.find(dd => dd.name === name);
            if (d) newSubjects.push({ id: d.id, type: "deal", name: d.name, sub: d.company });
          });
          setSubjects(newSubjects);
        }
      }

      setSearchValue("");
      setShowSearchDropdown(false);
    }
  }, [taskId, prevTaskId, task, backendTask, contextTasks, parseNotesString, resolveTaskNotes, contextUpdateTask]);
  // Note: isLovablePreview is a utility function, not a reactive value

  if (!task) return null;

  const parsed = parseDate(dueDate);
  const pIcon = priority === "High" ? <ArrowUp className="h-3.5 w-3.5" /> : priority === "Medium" ? <ArrowRight className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;
  const pColor = priority === "High" ? "text-rose-600 bg-rose-500/10 border-rose-500/20" : priority === "Medium" ? "text-orange-500 bg-orange-500/10 border-orange-500/20" : "text-amber-500 bg-amber-500/10 border-amber-500/20";

  return (
    <>
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent data-testid="task-detail-sheet" side="right" className="w-[50vw] sm:max-w-none p-0 overflow-y-auto [&>button]:hidden" hideOverlay={hideOverlay} onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="p-6 animate-fade-in" ref={sheetInnerRef}>
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-3 transition">
              <ArrowLeft className="h-4 w-4" /> {t("detailSheets.back")}
            </button>
          )}
          <div className="flex items-center justify-between mb-6">
            <input
              value={title}
              onChange={e => {
                setTitle(e.target.value);
              }}
              onBlur={async () => {
                if (!isDataLoaded || !task) return; // Prevent save until data is loaded
                if (title !== task.title) {
                  markChanged();
                  if (isLovablePreview()) {
                    contextUpdateTask(task.id, { title });
                  } else {
                    const payload = buildUpdatePayload({ title });
                    await updateTask(task.id, payload);
                  }
                }
              }}
              className="text-2xl font-display font-bold text-foreground bg-transparent border-none outline-none focus:ring-0 w-full mr-3"
              placeholder={t("detailSheets.taskTitlePlaceholder")}
            />
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => setConfirmDeleteOpen(true)}
                className="text-muted-foreground hover:text-destructive transition"
                title={t("detailSheets.deleteTask")}
                aria-label={t("detailSheets.deleteTask")}
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button onClick={() => handleSheetOpenChange(false)} className="text-muted-foreground hover:text-foreground transition">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Whisper attribution — mirrors the origin icon shown at the start
              of each row on the tasks list. Shows who assigned the task, that
              Coffee AI suggested it, or that the owner created it manually. */}
          {(() => {
            const assignedByOther = !!task.assignedBy && task.assignedBy !== assignee;
            if (assignedByOther) {
              return (
                <div className="-mt-4 mb-6 flex items-center gap-2 text-sm text-muted-foreground">
                  <UserAvatar name={task.assignedBy!} size="xs" />
                  <span>
                    <Trans
                      i18nKey="detailSheets.assignedBy"
                      values={{ name: task.assignedBy }}
                      components={{ name: <span className="text-foreground font-medium" /> }}
                    />
                    {task.assignedAt && (
                      <>
                        <span className="mx-1.5 text-muted-foreground/60">·</span>
                        <span>{task.assignedAt}</span>
                      </>
                    )}
                  </span>
                </div>
              );
            }
            if (task.origin === "ai") {
              return (
                <div className="-mt-4 mb-6 flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-accent/15 text-accent">
                    <Sparkles className="h-3 w-3" />
                  </span>
                  <span>
                    {/* Two whole sentences rather than a translated suffix glued
                        onto a stem — the meeting clause lands in a different
                        position in several languages. */}
                    <Trans
                      i18nKey={
                        task.aiSource
                          ? "detailSheets.suggestedByAiFromMeeting"
                          : "detailSheets.suggestedByAi"
                      }
                      values={{
                        meetingDate: task.aiSource?.meetingDate,
                        personName: task.aiSource?.personName,
                      }}
                      components={{ product: <span className="text-foreground font-medium" /> }}
                    />
                  </span>
                </div>
              );
            }
            return (
              <div className="-mt-4 mb-6 flex items-center gap-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-muted text-muted-foreground">
                  <Plus className="h-3 w-3" />
                </span>
                <span>{t("detailSheets.createdByYou")}</span>
              </div>
            );
          })()}

          {/* Properties grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-1">{t("common.status")}</p>
              <span className={cn("text-sm font-medium", task.completed ? "text-success" : "text-foreground")}>
                {task.completed ? t("detailSheets.statusCompleted") : t("common.open")}
              </span>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-1">{t("common.priority")}</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={cn("flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium transition hover:opacity-80", pColor)}>
                    {pIcon}{t(PRIORITY_LABEL_KEYS[priority] ?? "common.medium")}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[130px]">
                  {priorityCycle.map(p => {
                    const icon = p === "High" ? <ArrowUp className="h-3.5 w-3.5 text-rose-600" /> : p === "Medium" ? <ArrowRight className="h-3.5 w-3.5 text-orange-500" /> : <ArrowDown className="h-3.5 w-3.5 text-amber-500" />;
                    return (
                      <DropdownMenuItem key={p} onClick={async () => {
                        if (!isDataLoaded || !task) return; // Prevent save until data is loaded
                        setPriority(p);
                        markChanged();
                        if (isLovablePreview()) {
                          contextUpdateTask(task.id, { priority: p });
                        } else {
                          const priorityEnum = p === 'High' ? NotePriorityEnum.High
                                            : p === 'Medium' ? NotePriorityEnum.Medium
                                            : NotePriorityEnum.Low;
                          const payload = buildUpdatePayload({ priority: priorityEnum });
                          await updateTask(task.id, payload);
                        }
                      }} className={cn("gap-2", priority === p && "font-medium text-accent")}>
                        {icon}{t(PRIORITY_LABEL_KEYS[p])}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-1">{t("tasks.dueDate")}</p>
              <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-sm text-foreground hover:text-accent transition">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {/* `dueDate` state stays in the stored "MMM d, yyyy" shape —
                        it round-trips through TasksContext and back through
                        `parseDate` — so only the rendering is localized. */}
                    {parsed ? formatDate(parsed) : <span className="text-muted-foreground">{t("detailSheets.noDueDate")}</span>}
                  </button>
                </PopoverTrigger>
                <PopoverPrimitive.Portal container={sheetInnerRef.current}>
                <PopoverPrimitive.Content align="start" sideOffset={4} className="z-[70] w-auto rounded-md border bg-popover text-popover-foreground shadow-md outline-none">
                  <Calendar
                    mode="single"
                    selected={parsed || undefined}
                    onSelect={async (d) => {
                      if (!isDataLoaded || !task) return; // Prevent save until data is loaded
                      if (d && d >= startOfDay(new Date())) {
                        setDueDate(format(d, "MMM d, yyyy"));
                        markChanged();
                        if (isLovablePreview()) {
                          contextUpdateTask(task.id, { dueDate: format(d, "MMM d, yyyy") });
                        } else {
                          const payload = buildUpdatePayload({ dueDate: d.toISOString() });
                          await updateTask(task.id, payload);
                        }
                        setDueDateOpen(false);
                      }
                    }}
                    disabled={{ before: new Date() }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                  <div className="border-t border-border p-2">
                    <button
                      onClick={async () => {
                        if (!isDataLoaded || !task) return; // Prevent save until data is loaded
                        setDueDate("");
                        markChanged();
                        if (isLovablePreview()) {
                          contextUpdateTask(task.id, { dueDate: "" });
                        } else {
                          const payload = buildUpdatePayload({ dueDate: "" });
                          await updateTask(task.id, payload);
                        }
                        setDueDateOpen(false);
                      }}
                      className="w-full text-xs text-destructive hover:bg-destructive/10 rounded-md px-3 py-1.5 transition text-left"
                    >
                      {t("detailSheets.clearDueDate")}
                    </button>
                  </div>
                </PopoverPrimitive.Content>
                </PopoverPrimitive.Portal>
              </Popover>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-1">{t("tasks.assignee")}</p>
              <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-sm text-foreground hover:text-accent transition group">
                    <UserAvatar name={assignee} size="xs" />
                    <span className="truncate">{assignee || <span className="text-muted-foreground">{t("detailSheets.unassigned")}</span>}</span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverPrimitive.Portal container={sheetInnerRef.current}>
                <PopoverPrimitive.Content align="start" sideOffset={4} className="z-[70] w-56 rounded-md border bg-popover text-popover-foreground shadow-md outline-none">
                  <div className="flex items-center border-b border-border px-2.5 py-2">
                    <Search className="h-3.5 w-3.5 text-muted-foreground mr-2 shrink-0" />
                    <input
                      ref={assigneeInputRef}
                      value={assigneeSearch}
                      onChange={e => setAssigneeSearch(e.target.value)}
                      placeholder={t("detailSheets.searchMembers")}
                      className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto p-1">
                    {filteredAssigneeMembers.map(m => (
                      <button
                        key={m.id ?? m.fullName}
                        onClick={async () => {
                          if (!isDataLoaded || !task) return;
                          setAssignee(m.fullName);
                          markChanged();
                          if (isLovablePreview()) {
                            contextUpdateTask(task.id, { assignee: m.fullName });
                          } else if (m.id) {
                            await updateTask(task.id, { userId: m.id }, false);
                          }
                          toast.success(t("detailSheets.taskAssignedTo", { name: m.fullName }));
                          setAssigneeOpen(false);
                        }}
                        className={cn(
                          "w-full text-left text-sm px-2.5 py-1.5 rounded-sm hover:bg-accent/10 flex items-center gap-2 transition",
                          m.fullName === assignee && "font-medium text-accent"
                        )}
                      >
                        <UserAvatar name={m.fullName} size="xs" />
                        <span className="truncate flex-1">{m.fullName}</span>
                        {m.fullName === assignee && <Check className="h-3.5 w-3.5 text-accent shrink-0" />}
                      </button>
                    ))}
                    {filteredAssigneeMembers.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-3">{t("detailSheets.noMembersFound")}</p>
                    )}
                  </div>
                </PopoverPrimitive.Content>
                </PopoverPrimitive.Portal>
              </Popover>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("common.notes")}</h2>
            <div className="w-full rounded-lg border border-border bg-card min-h-[140px]">
              <NoteEditor
                // NoteEditor only reads initialContent at mount (see its useMemo),
                // so the key must change once the backend blocks actually load —
                // otherwise the editor mounts empty (before getTask resolves) and
                // never picks up the loaded description. Keying on isDataLoaded
                // forces a remount with the parsed blocks once they arrive.
                key={`${taskId}-${isDataLoaded}`}
                initialContent={notesBlocks}
                onChange={handleNotesChange}
                autoFocus={false}
              />
            </div>
          </div>

          {/* Associations */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("common.associations")}</h2>
            <div className="space-y-3">
              {/* Subject Pills */}
              {subjects.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {subjects.map((subject) => (
                    <div key={`${subject.type}-${subject.id}`} data-testid="task-association-pill" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-card text-xs">
                      {subject.type === "person" && <UserAvatar name={subject.name} size="xs" />}
                      {subject.type === "company" && <CompanyLogo name={subject.name} domain={subject.sub} />}
                      {subject.type === "deal" && <Handshake className="h-4 w-4 text-muted-foreground" />}
                      <span>{subject.name}</span>
                      <button
                        onClick={() => handleRemoveSubject(subject.id, subject.type)}
                        className="ml-0.5 hover:text-foreground text-muted-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Unified Search */}
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchValue}
                    onChange={e => {
                      setSearchValue(e.target.value);
                      setShowSearchDropdown(true);
                    }}
                    onFocus={() => setShowSearchDropdown(true)}
                    placeholder={t("detailSheets.searchSubjectPlaceholder")}
                    className="pl-9"
                  />
                  {searchValue && (
                    <button
                      onClick={() => {
                        setSearchValue("");
                        setShowSearchDropdown(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Search Dropdown */}
                {showSearchDropdown && searchValue && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                    {searchLoading && !isLovablePreview() && (
                      <div className="text-center py-8 text-muted-foreground text-xs">{t("detailSheets.searching")}</div>
                    )}

                    {!searchLoading && !hasBackendResults && filteredItems.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-xs">{t("common.noResults")}</div>
                    )}

                    {(!searchLoading || isLovablePreview()) && (hasBackendResults || filteredItems.length > 0) && (
                      <div className="p-2">
                        {/* Backend Results - Contacts */}
                        {searchContacts.map((contact) => (
                          <button
                            key={contact.id}
                            onClick={() => {
                              handleSubjectSelected(contact as Contact);
                              setSearchValue("");
                              setShowSearchDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                          >
                            <UserAvatar
                              name={`${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim()}
                              src={contact.profileImageUrl}
                              size="sm"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {`${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {contact.accounts?.edges?.[0]?.node?.properties?.name || contact.accounts?.edges?.[0]?.node?.domain || ''}
                              </div>
                            </div>
                            <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("common.person")}</span>
                          </button>
                        ))}

                        {/* Backend Results - Accounts */}
                        {searchAccounts.map((account) => (
                          <button
                            key={account.id}
                            onClick={() => {
                              handleSubjectSelected(account as Account);
                              setSearchValue("");
                              setShowSearchDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                          >
                            <CompanyLogo
                              name={account.properties?.name || account.domain || ''}
                              domain={account.domain}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {account.properties?.name || account.domain}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {account.domain}
                              </div>
                            </div>
                            <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("common.company")}</span>
                          </button>
                        ))}

                        {/* Backend Results - Deals */}
                        {searchDeals.map((deal) => (
                          <button
                            key={deal.id}
                            onClick={() => {
                              handleSubjectSelected(deal as Deal);
                              setSearchValue("");
                              setShowSearchDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                          >
                            <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                              <Handshake className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {deal.properties?.name || t("detailSheets.unnamedDeal")}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {deal.accounts?.edges?.[0]?.node?.properties?.name || ''}
                              </div>
                            </div>
                            <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("common.deal")}</span>
                          </button>
                        ))}

                        {/* Dummy data fallback (for preview mode) */}
                        {filteredItems.map((item) => (
                          <button
                            key={`${item.type}-${item.id}`}
                            onClick={() => handleSubjectSelected(item)}
                            className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                          >
                            {item.type === "person" && <UserAvatar name={item.name} size="sm" />}
                            {item.type === "company" && <CompanyLogo name={item.name} domain={item.sub} />}
                            {item.type === "deal" && (
                              <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                                <Handshake className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{item.name}</div>
                              {item.sub && (
                                <div className="text-xs text-muted-foreground truncate">{item.sub}</div>
                              )}
                            </div>
                            <span className="text-[10px] uppercase text-muted-foreground tracking-wider">
                              {t(SUBJECT_TYPE_LABEL_KEYS[item.type])}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    <ConfirmDeleteDialog
      open={confirmDeleteOpen}
      onOpenChange={setConfirmDeleteOpen}
      title={t("detailSheets.deleteTaskTitle")}
      description={t("detailSheets.deleteTaskDescription")}
      onConfirm={async () => {
        if (!task) return;
        // Preview mode has no backing store to delete from; just close.
        if (isLovablePreview()) {
          handleSheetOpenChange(false);
          return;
        }
        try {
          await deleteTask(task.id);
          handleSheetOpenChange(false);
        } catch {
          // deleteTask surfaces its own error toast.
        }
      }}
    />
    </>
  );
}
